import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {useSelectedConversationsDetails} from '../../../../../../../libs/inboxes/business/src/cells/conversations';
import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  IntersperseItem,
  intersperseNodes,
} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isNotNull} from '../../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {onlyItem} from '../../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {ConversationSummaryStatesEnum} from '../../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {InboxModel} from '../../../../../../../libs/shared/core/src/models/api/inboxModel';
import {PaletteColorsEnum} from '../../../../../../../libs/shared/core/src/models/api/paletteModel';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {getUserTimeZone} from '../../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {getUser} from '../../../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {getHasConversationSynopsis} from '../../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownSeparator} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {useClipboard} from '../../../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  buildCopyLinkDataTestId,
  generateConversationPermalink,
} from '../../../../../../../srcCommon/helpers/premalinksHelpers';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {
  useAssignBulkActionPermission,
  useConversationStatusBulkActionPermission,
  useMergeBulkActionPermission,
  useSeenBulkActionPermission,
  useSubscribeBulkActionPermission,
} from '../../../../../../../srcCommon/permissions/useBulkActionPermission';
import {
  useConversationSeenActionOfflinePermission,
  useConversationStatusActionOfflinePermission,
} from '../../../../../../../srcCommon/permissions/useOfflinePermission';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {
  areConversationsUnread,
  buildConversationPublicId,
  computeConversationsMergeError,
  ConversationsMergeCheckErrorsEnum,
  findConversationNamespaces,
  isConversationInTeamContext,
} from '../../../../../../helpers/models/conversationHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {VisualRecipientModel} from '../../../../../../models/api/visualRecipientModel';
import {openConversationPrintPopup} from '../../../../../../services/popups';
import {
  getCompanyPreferences,
  getHasAISummarizeFeature,
  getIsAiSummarizeEnabled,
  getIsAiSynopsisEnabled,
} from '../../../../../../store/data/company/companySelectors';
import {manuallySummarizeConversationAsyncAction} from '../../../../../../store/data/conversations/conversationsActions';
import {computeIsUnableToSummarizeConversation} from '../../../../../../store/data/conversations/conversationsHelpers';
import {getHasLinkedConversations} from '../../../../../../store/data/conversations/linkedConversationsSelectors';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {PermissionDropdownItem} from '../../../../../common/dropdowns/permissionDropdownItem';
import {PermissionSimpleDropdownItem} from '../../../../../common/dropdowns/permissionSimpleDropdownItem';
import {TooltipLabelWithShortcut} from '../../../../../common/tooltips/tooltipLabelWithShortcut';
import {LinkConversationsMoreDropdownItem} from '../../linkedConversations/linkConversationsDropdownMoreItem';
import {LinkOrUnlinkConversationsDropdownMoreItem} from '../../linkedConversations/linkOrUnlinkConversationsDropdownMoreItem';
import {PermalinkPermissionTooltip} from './permalinkPermissionTooltip';

/*
 * Props.
 */

export interface ConversationsMoreDropdownPublicProps {
  panelId: ViewerPanelId;
  contextNamespaces: ReadonlyArray<string>;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  onlyConversation: ConversationModel | undefined;
  conversations: ReadonlyArray<ConversationBaseModel>;
  recipient: VisualRecipientModel | undefined;
  recipients: ReadonlyArray<VisualRecipientModel | undefined>;
  areAllSpam: boolean;
  areAllInternal: boolean;
  areAllSubscribed: boolean;
  areAllSnoozed: boolean;
  areAllStarred: boolean;
  canPopout: boolean;
  onUnblockRecipientClick: () => void;
  onPopoutClick: () => void;
  isBulkActionRequired: boolean;
  onCollapseClick: () => void;
  onExpandClick: () => void;
  canExpandAll: boolean;
  isAllExpanded: boolean;
  isAllCollapsed: boolean;
}

interface ConversationsMoreDropdownProps extends DropdownProps, ConversationsMoreDropdownPublicProps {
  onSnoozeClick?: () => void;
  onSubscribeClick?: () => void;
  onUnsubscribeClick?: () => void;
  onAssignClick?: () => void;
  onStarClick?: () => void;
  onUnstarClick?: () => void;
  onMergeClick: () => void;
  onReportPhishingClick: () => void;
  onBlockRecipientClick: () => void;
  onOpenConversationSummaryClick: () => void;
  onOpenConversationAttachmentsClick: () => void;
  onMarkAsReadClick: () => void;
  onMarkAsUnreadClick: () => void;
  onMarkAsSpamClick: () => void;
  isInMultiConversationsViewer?: boolean;
  onLinkConversationsClick: () => void;
}

/*
 * Styles.
 */

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  flex-flow: row;
  gap: 4px;
  align-items: center;
`;

/*
 * Component.
 */

export const ConversationsMoreDropdown: FC<ConversationsMoreDropdownProps> = (props) => {
  const hasLinkedConversations = useAppSelector(getHasLinkedConversations);

  if (props.conversations.length > 1) {
    if (props.isInMultiConversationsViewer) {
      return (
        <SimpleDropdown {...props} placement="bottom-end">
          <MarkAsUnreadItem {...props} />
          <MarkAsReadItem {...props} />
          <DropdownSeparator />
          {props.onSnoozeClick && (
            <SnoozeItem
              cellsContext={props.cellsContext}
              conversations={props.conversations}
              onSnooze={props.onSnoozeClick}
              namespaces={props.contextNamespaces}
              areAllSnoozed={props.areAllSnoozed}
              inboxesParameters={props.inboxesParameters}
              isBulkActionRequired={props.isBulkActionRequired}
            />
          )}
          {props.onAssignClick && (
            <AssignItem
              onAssign={props.onAssignClick}
              namespaces={props.contextNamespaces}
              inboxesParameters={props.inboxesParameters}
              isBulkActionRequired={props.isBulkActionRequired}
            />
          )}
          {props.onSubscribeClick && props.onUnsubscribeClick && (
            <SubscribeItem
              onSubscribe={props.onSubscribeClick}
              onUnsubscribe={props.onUnsubscribeClick}
              areAllSubscribed={props.areAllSubscribed}
              inboxesParameters={props.inboxesParameters}
              isBulkActionRequired={props.isBulkActionRequired}
            />
          )}
          <MergeItem {...props} />
          {hasLinkedConversations && (
            <LinkConversationsMoreDropdownItem {...props} selectedConversations={props.conversations} />
          )}
          <DropdownSeparator />
          <MarkAsSpamItem {...props} />
        </SimpleDropdown>
      );
    }

    return (
      <SimpleDropdown {...props} placement="bottom-end">
        <MarkAsUnreadItem {...props} />
        <MarkAsReadItem {...props} />
        <MergeItem {...props} />
      </SimpleDropdown>
    );
  }

  const firstSection: IntersperseItem = {
    node: renderFirstSection(props),
    key: 'first',
  };

  const secondSection: IntersperseItem = {
    node: renderSecondSection(props, hasLinkedConversations),
    key: 'second',
  };

  const thirdSection: IntersperseItem = {
    node: renderThirdSection(props),
    key: 'third',
  };

  const sectionsToRender = [firstSection, secondSection, thirdSection].filter((section) =>
    isNotNull(section.node),
  );

  return (
    <SimpleDropdown {...props} placement="bottom-end">
      {intersperseNodes(sectionsToRender, () => (
        <DropdownSeparator />
      ))}
    </SimpleDropdown>
  );
};

function renderFirstSection(props: ConversationsMoreDropdownProps) {
  return (
    <>
      <ToggleUnread {...props} />
      <ExpandAllItem {...props} />
      <PrintItem {...props} />
    </>
  );
}

function renderSecondSection(
  props: ConversationsMoreDropdownProps,
  shouldRenderLinkedConversationsItem: boolean,
) {
  return (
    <>
      {shouldRenderLinkedConversationsItem && (
        <LinkOrUnlinkConversationsDropdownMoreItem {...props} selectedConversations={props.conversations} />
      )}
      <CopyItems {...props} />
    </>
  );
}

function renderThirdSection(props: ConversationsMoreDropdownProps) {
  // We either render GenerateConversationSummaryItem or GenerateConversationSynopsisItem.
  return (
    <>
      <GenerateConversationSummaryItem {...props} />
      <GenerateConversationSynopsisItem {...props} />
      <OpenConversationSummaryItem {...props} />
      <OpenConversationAttachmentsSummaryItem {...props} />
      <PopoutItem {...props} />
    </>
  );
}

/*
 * Snooze.
 */

interface SnoozeItemProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  onSnooze: () => void;
  namespaces: ReadonlyArray<string>;
  areAllSnoozed: boolean;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
}

const SnoozeItem: FC<SnoozeItemProps> = (props) => {
  const theme = useUserTheme();
  const {namespaces, onSnooze, areAllSnoozed} = props;
  const bulkActionPermission = useConversationStatusBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  const {shouldDisableBulkActions} = useSelectedConversationsDetails(props);
  const isDisabled = shouldDisableBulkActions;

  const label = areAllSnoozed ? (
    <Trans id="conversation-actions-unsnooze">Unsnooze</Trans>
  ) : (
    <Trans id="conversation-actions-snooze">Snooze</Trans>
  );

  const renderTooltip = () => {
    if (!isDisabled) {
      return null;
    }

    return (
      <Trans id="conversation-actions-sooze-disabled-mixed-conversation-type">
        Can&apos;t snooze because not all selected conversations have ticket statuses.
      </Trans>
    );
  };

  return (
    <PermissionDropdownItem
      namespaces={namespaces}
      permission={PermissionsEnum.CONVERSATION_ARCHIVE}
      onSelect={onSnooze}
      bulkActionPermission={bulkActionPermission}
      isDisabled={isDisabled}
      renderTooltip={renderTooltip}
    >
      <DropdownItemIcon iconName="snoozeSmall" color={theme.greys.shade80} iconSize={16} label={label} />

      <DropdownItemContent>{label}</DropdownItemContent>
    </PermissionDropdownItem>
  );
};

/*
 * Assign.
 */

interface AssignItemProps {
  onAssign: () => void;
  namespaces: ReadonlyArray<string>;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
}

const AssignItem: FC<AssignItemProps> = (props) => {
  const theme = useUserTheme();
  const {namespaces, onAssign} = props;
  const bulkActionPermission = useAssignBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  return (
    <PermissionDropdownItem
      namespaces={namespaces}
      permission={PermissionsEnum.CONVERSATION_ASSIGN}
      onSelect={onAssign}
      bulkActionPermission={bulkActionPermission}
    >
      <DropdownItemIcon
        iconName="assignSmall"
        color={theme.greys.shade80}
        iconSize={16}
        label={<Trans id="conversation-actions-assign">Assign</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-assign">Assign</Trans>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );
};

/*
 * Subscribe.
 */

interface SubscribeItemProps {
  onSubscribe: () => void;
  onUnsubscribe: () => void;
  areAllSubscribed: boolean;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
}

const SubscribeItem: FC<SubscribeItemProps> = (props) => {
  const theme = useUserTheme();
  const {areAllSubscribed, onSubscribe, onUnsubscribe} = props;
  const bulkActionPermission = useSubscribeBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  const muteItem = (
    <PermissionDropdownItem onSelect={onUnsubscribe} bulkActionPermission={bulkActionPermission}>
      <DropdownItemIcon
        iconName="mute"
        color={theme.greys.shade80}
        iconSize={16}
        label={<Trans id="conversation-actions-unsubscribe">Unsubscribe</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-unsubscribe">Unsubscribe</Trans>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );
  const subscribeItem = (
    <PermissionDropdownItem onSelect={onSubscribe} bulkActionPermission={bulkActionPermission}>
      <DropdownItemIcon
        iconName="subscribed"
        color={theme.greys.shade80}
        iconSize={16}
        label={<Trans id="conversation-actions-subscribe">Subscribe</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-subscribe">Subscribe</Trans>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );

  if (areAllSubscribed) {
    return muteItem;
  }

  return (
    <>
      {subscribeItem}
      {muteItem}
    </>
  );
};

/*
 * Print.
 */

const PrintItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {conversations, areAllInternal} = props;
  const conversation = onlyItem(conversations);
  const userTimeZone = useAppSelector(getUserTimeZone);

  if (!conversation) {
    return null;
  }

  return (
    <PermissionDropdownItem
      onSelect={() => openConversationPrintPopup(conversation, userTimeZone)}
      isDisabled={areAllInternal}
    >
      <DropdownItemIcon
        iconSize={16}
        color={theme.greys.shade80}
        iconName="printSmall"
        label={<Trans id="conversation-actions-print">Print</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-print">Print</Trans>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );
};

/*
 * Toggle unread.
 */

const ToggleUnread: FC<ConversationsMoreDropdownProps> = (props) => {
  const {cellsContext, conversations} = props;

  if (areConversationsUnread(cellsContext, conversations)) {
    return <MarkAsReadItem {...props} />;
  }

  return <MarkAsUnreadItem {...props} />;
};

const MarkAsReadItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();
  const offlinePermission = useConversationSeenActionOfflinePermission();
  const bulkActionPermission = useSeenBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );
  return (
    <PermissionDropdownItem
      onSelect={props.onMarkAsReadClick}
      offlinePermission={offlinePermission}
      bulkActionPermission={bulkActionPermission}
    >
      <DropdownItemIcon
        iconSize={16}
        color={theme.greys.shade80}
        iconName="readSmall"
        label={<Trans id="conversation-actions-markasread">Mark as read</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-markasread">Mark as read</Trans>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );
};

const MarkAsUnreadItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();
  const offlinePermission = useConversationSeenActionOfflinePermission();
  const bulkActionPermission = useSeenBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  return (
    <PermissionDropdownItem
      onSelect={props.onMarkAsUnreadClick}
      offlinePermission={offlinePermission}
      bulkActionPermission={bulkActionPermission}
    >
      <DropdownItemIcon
        iconSize={16}
        color={theme.greys.shade80}
        iconName="messageSmall"
        label={<Trans id="conversation-actions-markasunread">Mark as unread</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-markasunread">Mark as unread</Trans>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );
};

/*
 * Merge.
 */

const MergeItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {conversations, onMergeClick} = props;
  const mergeCheckError = computeConversationsMergeError(conversations);

  const bulkActionPermission = useMergeBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  // If it's a single conversation, hide the item.
  if (mergeCheckError === ConversationsMergeCheckErrorsEnum.SINGLE_CONVERSATION) {
    return null;
  }

  // If conversations can't be merged, disable the item.
  if (mergeCheckError !== ConversationsMergeCheckErrorsEnum.NONE) {
    return (
      <PermissionDropdownItem
        isDisabled
        renderTooltip={() => renderCannotMergeReason(mergeCheckError)}
        bulkActionPermission={bulkActionPermission}
      >
        <DropdownItemIcon
          iconName="mergeSmall"
          iconSize={16}
          color={theme.greys.shade80}
          label={<Trans id="conversation-actions-merge">Merge conversations</Trans>}
        />
        <DropdownItemContent>
          <Trans id="conversation-actions-merge">Merge conversations</Trans>
        </DropdownItemContent>
      </PermissionDropdownItem>
    );
  }

  // Otherwise, display the normal item.
  return (
    <PermissionDropdownItem onSelect={onMergeClick} bulkActionPermission={bulkActionPermission}>
      <DropdownItemIcon
        iconName="mergeSmall"
        iconSize={16}
        color={theme.greys.shade80}
        label={<Trans id="conversation-actions-merge">Merge conversations</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-merge">Merge conversations</Trans>
      </DropdownItemContent>
    </PermissionDropdownItem>
  );
};

function renderCannotMergeReason(mergeCheckError: ConversationsMergeCheckErrorsEnum) {
  switch (mergeCheckError) {
    case ConversationsMergeCheckErrorsEnum.MESSAGE_TYPE_MISMATCH:
      return <Trans id="merge-error-mismatch">Only conversations of the same type can be merged.</Trans>;

    case ConversationsMergeCheckErrorsEnum.UNSUPPORTED_CHANNEL:
    default:
      return <Trans id="merge-error-channel">Only Email and Tweet conversations can be merged.</Trans>;
  }
}

/*
 * Mark as spam.
 */

const MarkAsSpamItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {contextNamespaces, cellsContext, conversations, onMarkAsSpamClick, areAllInternal, areAllSpam} =
    props;
  const offlinePermission = useConversationStatusActionOfflinePermission(cellsContext, conversations);

  return (
    <PermissionCheck
      namespaces={contextNamespaces}
      permission={PermissionsEnum.CONVERSATION_TRASH}
      offlinePermission={offlinePermission}
    >
      {(permission) => (
        <DropdownItem
          onSelect={onMarkAsSpamClick}
          isDisabled={!permission.isAllowed || areAllInternal || areAllSpam}
        >
          <DropdownItemIcon
            iconName="spamSmall"
            color={theme.greys.shade80}
            iconSize={16}
            label={<Trans id="conversation-actions-markasspam">Mark as spam</Trans>}
          />
          <DropdownItemContent>
            <Trans id="conversation-actions-markasspam">Mark as spam</Trans>
          </DropdownItemContent>
        </DropdownItem>
      )}
    </PermissionCheck>
  );
};

/*
 * Report phishing.
 */

interface ReportPhishingItemProps {
  contextNamespaces: ReadonlyArray<string>;
  areAllInternal: boolean;
  onReportPhishingClick: (() => void) | undefined;
  hasIcon?: boolean;
}

export const ReportPhishingDropdownItem: FC<ReportPhishingItemProps> = (props) => {
  const theme = useUserTheme();
  const {contextNamespaces, areAllInternal, onReportPhishingClick} = props;

  if (!onReportPhishingClick) {
    return null;
  }

  return (
    <PermissionCheck namespaces={contextNamespaces} permission={PermissionsEnum.CONVERSATION_TRASH}>
      {(permission) => (
        <DropdownItem onSelect={onReportPhishingClick} isDisabled={!permission.isAllowed || areAllInternal}>
          {props.hasIcon && (
            <DropdownItemIcon
              iconName="phishingSmall"
              iconSize={16}
              color={theme.greys.shade80}
              label={<Trans id="conversation-actions-reportphishing">Report phishing</Trans>}
            />
          )}
          <DropdownItemContent>
            <Trans id="conversation-actions-reportphishing">Report phishing</Trans>
          </DropdownItemContent>
        </DropdownItem>
      )}
    </PermissionCheck>
  );
};

/*
 * Block recipient.
 */

interface BlockRecipientItemProps {
  recipient: VisualRecipientModel;
  cellsContext: CellsContext;
  onBlockRecipientClick: (() => void) | undefined;
  onUnblockRecipientClick: (() => void) | undefined;
  hasIcon?: boolean;
}

export const BlockRecipientDropdownItem: FC<BlockRecipientItemProps> = (props) => {
  const theme = useUserTheme();
  const {cellsContext, recipient, onBlockRecipientClick, onUnblockRecipientClick} = props;
  const namespaces = cellsContext.namespace ? [cellsContext.namespace] : [];

  // Check if recipient is a spammer in the current namespace or if we are in the spam tab
  if ((recipient.isSpammer && namespaces.includes(recipient.namespace)) || cellsContext.inSpam) {
    if (!onUnblockRecipientClick) {
      return null;
    }

    return (
      <PermissionSimpleDropdownItem onSelect={onUnblockRecipientClick}>
        <Trans id="conversation-actions-unblocksender">Unblock sender</Trans>
      </PermissionSimpleDropdownItem>
    );
  }

  if (!onBlockRecipientClick || namespaces.length === 0) {
    return null;
  }

  // Otherwise, check for permission.
  return (
    <PermissionCheck namespaces={namespaces} permission={PermissionsEnum.BLOCK_CONTACT}>
      {(permission) => (
        <DropdownItem
          onSelect={onBlockRecipientClick}
          isDisabled={!permission.isAllowed || !recipient || cellsContext.isDelegation}
        >
          {props.hasIcon && (
            <DropdownItemIcon
              iconName="noneSmall"
              label={<Trans id="conversation-actions-blocksender">Block sender</Trans>}
              iconSize={16}
              color={theme.greys.shade80}
            />
          )}
          <DropdownItemContent>
            <Trans id="conversation-actions-blocksender">Block sender</Trans>
          </DropdownItemContent>
        </DropdownItem>
      )}
    </PermissionCheck>
  );
};

/*
 * Permanently delete conversation
 */

interface DestroyConversationDropdownItemProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  onDestroyClick: (() => void) | undefined;
}

export const DestroyConversationDropdownItem: FC<DestroyConversationDropdownItemProps> = (props) => {
  const theme = useUserTheme();
  const {cellsContext, conversations, conversationsInboxes, onDestroyClick} = props;

  return (
    <PermissionCheck
      namespaces={findConversationNamespaces(props.conversations)}
      permission={PermissionsEnum.CONVERSATION_DESTROY}
    >
      {(permission) => (
        <DropdownItem onSelect={onDestroyClick} isDisabled={!permission.isAllowed || !onDestroyClick}>
          <DropdownItemIcon
            iconName="trashSmall"
            color={theme.palette.red.shade40}
            label={<Trans id="conversation-actions-destroygeneric">Delete permanently</Trans>}
            iconSize={16}
          />
          <DropdownItemContent color={PaletteColorsEnum.RED}>
            {renderDestroyItemText(cellsContext, conversations, conversationsInboxes)}
          </DropdownItemContent>
        </DropdownItem>
      )}
    </PermissionCheck>
  );
};

function renderDestroyItemText(
  cellsContext: CellsContext,
  conversations: ReadonlyArray<ConversationBaseModel>,
  conversationsInboxes: ReadonlyArray<InboxModel>,
) {
  const areAllInTeamContext = conversations.every((c) => isConversationInTeamContext(cellsContext, c));

  if (cellsContext.isDelegation) {
    return <Trans id="conversation-actions-destroyinteammate">Delete permanently in teammate inbox</Trans>;
  }

  if (areAllInTeamContext) {
    if (conversationsInboxes.length === 1) {
      return (
        <Trans id="conversation-actions-destroyininbox">
          Delete permanently in {conversationsInboxes[0].name}
        </Trans>
      );
    }

    return (
      <Trans id="conversation-actions-destroyinmanyinboxes">
        Delete permanently in {conversationsInboxes.length} inboxes
      </Trans>
    );
  }

  return <Trans id="conversation-topbar-destroyinmyinbox">Delete permanently in my inbox</Trans>;
}

const OpenConversationSummaryItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();

  return (
    <DropdownItem
      onSelect={props.onOpenConversationSummaryClick}
      interactionId="viewConversationDetailsButton"
    >
      <DropdownItemIcon
        iconName="infoSmall"
        iconSize={16}
        color={theme.greys.shade80}
        label={<Trans id="conversation-actions-details">View conversation details</Trans>}
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-details">View conversation details</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
};

const GenerateConversationSummaryItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const {onlyConversation, cellsContext} = props;

  const isAiSummarizeEnabled = useAppSelector(getIsAiSummarizeEnabled);
  const hasFeature = useAppSelector(getHasAISummarizeFeature);
  const renderedText = <Trans id="conversation-actions-summarize-ai-feature">Summarize</Trans>;

  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();
  const user = useAppSelector(getUser);

  const {isSummarizeDisabled, hasSummaryAlready, isInternalConversation, hasNoMessages, isTrashed} =
    computeIsUnableToSummarizeConversation(cellsContext, onlyConversation);

  const hasConversationSynopsisFF = useAppSelector(getHasConversationSynopsis);
  const isAiSynopsisEnabled = useAppSelector(getIsAiSynopsisEnabled);

  if (
    !onlyConversation ||
    !hasFeature ||
    hasSummaryAlready ||
    isInternalConversation ||
    isTrashed ||
    (hasConversationSynopsisFF && isAiSynopsisEnabled)
  ) {
    return null;
  }

  const renderTooltip = () => {
    if (!isAiSummarizeEnabled) {
      return (
        <Trans id="conversation-actions-summarize-ai-feature-disabled">
          Your administrator has disabled this feature.
        </Trans>
      );
    }

    if (hasNoMessages) {
      return (
        <Trans id="conversation-actions-summarize-no-messages">
          Summaries are only available to conversations with at least one message.
        </Trans>
      );
    }

    return (
      <TooltipLabelWithShortcut
        label={renderedText}
        keyboardAction={KeyboardActionsEnum.TOGGLE_EXPANDED_CONVERSATION_SUMMARY}
      />
    );
  };

  return (
    <DropdownItem
      onSelect={() => {
        trackDataEvent(DataEventActionsEnum.CLICK, ViewerDataEventObject.CONVERSATION_SUMMARY_GENERATE, {
          teammate_id: user.id,
          source: 'dropdown',
        });
        dispatch(manuallySummarizeConversationAsyncAction(onlyConversation.id));
      }}
      interactionId="generateConversationSummaryButton"
      isDisabled={
        !isAiSummarizeEnabled ||
        onlyConversation.summaryState === ConversationSummaryStatesEnum.LOADING ||
        isSummarizeDisabled
      }
      renderTooltip={renderTooltip}
    >
      <DropdownItemIcon iconName="sparklesAiSmall" iconSize={16} color={theme.greys.shade80} label={null} />
      <StyledDropdownItemContent>{renderedText}</StyledDropdownItemContent>
    </DropdownItem>
  );
};

const GenerateConversationSynopsisItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const {onlyConversation, cellsContext} = props;

  const hasConversationSynopsisFF = useAppSelector(getHasConversationSynopsis);
  const isAiSynopsisEnabled = useAppSelector(getIsAiSynopsisEnabled);

  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();
  const user = useAppSelector(getUser);

  const {isSynopsisDisabled, hasOnelinerAlready, isInternalConversation, hasNoMessages, isTrashed} =
    computeIsUnableToSummarizeConversation(cellsContext, onlyConversation);

  if (
    !onlyConversation ||
    !(hasConversationSynopsisFF && isAiSynopsisEnabled) ||
    hasOnelinerAlready ||
    isInternalConversation ||
    isTrashed
  ) {
    return null;
  }

  const renderedText = <Trans id="conversation-actions-synopsis-ai-feature">Summarize</Trans>;

  const renderTooltip = () => {
    if (hasNoMessages) {
      return (
        <Trans id="conversation-actions-synopsis-no-messages">
          Summaries are only available to conversations with at least one message.
        </Trans>
      );
    }

    return renderedText;
  };

  return (
    <DropdownItem
      onSelect={() => {
        trackDataEvent(DataEventActionsEnum.CLICK, ViewerDataEventObject.CONVERSATION_SUMMARY_GENERATE, {
          teammate_id: user.id,
          source: 'dropdown',
        });
        dispatch(manuallySummarizeConversationAsyncAction(onlyConversation.id));
      }}
      interactionId="generateConversationSummaryButton"
      isDisabled={
        !isAiSynopsisEnabled ||
        onlyConversation.summaryState === ConversationSummaryStatesEnum.LOADING ||
        isSynopsisDisabled
      }
      renderTooltip={renderTooltip}
    >
      <DropdownItemIcon iconName="sparklesAiSmall" iconSize={16} color={theme.greys.shade80} label={null} />
      <StyledDropdownItemContent>{renderedText}</StyledDropdownItemContent>
    </DropdownItem>
  );
};

const OpenConversationAttachmentsSummaryItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();

  return (
    <DropdownItem
      onSelect={props.onOpenConversationAttachmentsClick}
      interactionId="viewConversationAttachmentsButton"
    >
      <DropdownItemIcon
        iconName="attachmentSmall"
        iconSize={16}
        color={theme.greys.shade80}
        label={<Trans id="conversation-actions-detailsattachments">View attachments</Trans>}
      />

      <DropdownItemContent>
        <Trans id="conversation-actions-detailsattachments">View attachments</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
};

/*
 * Popout.
 */

const PopoutItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();

  return (
    <DropdownItem onSelect={props.onPopoutClick}>
      <DropdownItemIcon
        label={<Trans id="conversation-actions-popout">View in external window</Trans>}
        iconSize={16}
        color={theme.greys.shade80}
        iconName="externalLinkSmall"
      />
      <DropdownItemContent>
        <Trans id="conversation-actions-popout">View in external window</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
};

/*
 * Expand/Collapse items.
 */

const ExpandAllItem: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {canExpandAll, isAllCollapsed, isAllExpanded, onCollapseClick, onExpandClick} = props;

  if (!canExpandAll) {
    return null;
  }

  return (
    <>
      <DropdownItem isDisabled={isAllExpanded} onSelect={onExpandClick}>
        <DropdownItemIcon
          iconName="expandSmall"
          iconSize={16}
          color={theme.greys.shade80}
          label={<Trans id="conversation-actions-expandall">Expand all</Trans>}
        />
        <DropdownItemContent>
          <Trans id="conversation-actions-expandall">Expand all</Trans>
        </DropdownItemContent>
      </DropdownItem>
      <DropdownItem isDisabled={isAllCollapsed} onSelect={onCollapseClick}>
        <DropdownItemIcon
          iconName="collapseSmall"
          iconSize={16}
          color={theme.greys.shade80}
          label={<Trans id="conversation-actions-collapseall">Collapse all</Trans>}
        />
        <DropdownItemContent>
          <Trans id="conversation-actions-collapseall">Collapse all</Trans>
        </DropdownItemContent>
      </DropdownItem>
    </>
  );
};

/*
 * Copy items.
 */

const CopyItems: FC<ConversationsMoreDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {copy} = useClipboard();
  const {conversations} = props;
  const conversation = onlyItem(conversations);
  const preferences = useAppSelector(getCompanyPreferences);

  /*
   * Event handlers.
   */

  const onCopyPublicIdClick = useCallback(() => {
    if (!conversation) {
      return;
    }

    copy(buildConversationPublicId(conversation));
  }, [conversation, copy]);

  const onCopyPublicLinkClick = useCallback(async () => {
    if (!conversation) {
      return;
    }

    copy(generateConversationPermalink(conversation));
  }, [conversation, copy]);

  if (!conversation) {
    return null;
  }

  return (
    <>
      <DropdownItem onSelect={onCopyPublicIdClick}>
        <DropdownItemIcon
          iconSize={16}
          color={theme.greys.shade80}
          iconName="copySmall"
          label={<Trans id="conversation-actions-copyid">Copy conversation ID</Trans>}
        />
        <DropdownItemContent>
          <Trans id="conversation-actions-copyid">Copy conversation ID</Trans>
        </DropdownItemContent>
      </DropdownItem>
      <DropdownItem
        onSelect={onCopyPublicLinkClick}
        interactionId={buildCopyLinkDataTestId('conversation', preferences)}
      >
        <DropdownItemIcon
          iconSize={16}
          color={theme.greys.shade80}
          iconName="linkSmall"
          label={<Trans id="conversation-actions-copylink">Copy conversation link</Trans>}
        />
        <DropdownItemContent renderTooltip={() => <PermalinkPermissionTooltip preferences={preferences} />}>
          <Trans id="conversation-actions-copylink">Copy conversation link</Trans>
        </DropdownItemContent>
      </DropdownItem>
    </>
  );
};
